import React, { useMemo, useState } from "react";
import { useRouteMatch } from "react-router-dom";
import { useQuery } from "@apollo/client";
import {
    GET_TRANSLATION_AI_LABEL_REQUESTS,
    GET_TRANSLATION_AI_REQUESTS
} from "graphql/query/translation-ai-gql";
import { GET_USERS } from "graphql/query/user-gql";
import Settings from "components/settings";
import { PageWrapDefault, Tables } from "components/layout";
import { Localize } from "components/service";
import { useActiveMenuItem, useBreadCrumbs, useDebounce } from "components/use-hooks";
import { gqlBuilderWhere } from "utils";
import TranslationAiLanguageRequestSummary from "./translation-ai-language-request-summary";

import './settings.scss';


const routeUrl = Settings.Const.translationAiRequestsPath;
const tableHelper = Settings.Helpers.TranslationAiTable.Requests;
const languageCacheHelper = Settings.Helpers.TranslationAiLanguageCache;
const analyticsRowsLimit = 500;

const TranslationAiRequestsPage = () => {

    useActiveMenuItem([ "settings-translation-ai-requests" ], [ "settings" ]);

    const breadcrumbs = [
        {
            label: Localize({ children: "BREADCRUMBS.Label_Settings" }).props.children,
            path: `${ Settings.Const.basePath }`
        },
        {
            label: Localize({ children: "BREADCRUMBS.Label_TranslationAiRequests" }).props.children,
            path: `${ routeUrl }`
        }
    ];

    useBreadCrumbs(breadcrumbs);

    let match = useRouteMatch(`${ routeUrl }/page/:pageNum`);

    const [ searchText, setSearchText ] = useState("");
    const [ filters, setFilters ] = useState();
    const [ records, setRecords ] = useState([]);
    const debouncedSearchText = useDebounce(searchText);

    const missingUserIds = useMemo(() => (
        [ ...new Set(records
            .filter(({ relUser, user_id }) => {
                const hasUserInfo = relUser?.name || relUser?.surname || relUser?.email;

                return !hasUserInfo && user_id;
            })
            .map(({ user_id }) => user_id)
        ) ]
    ), [ records ]);

    const { data: usersData } = useQuery(GET_USERS, {
        skip: !missingUserIds.length,
        variables: {
            first: missingUserIds.length,
            where: gqlBuilderWhere({
                userId: {
                    column: "ID",
                    operator: "IN",
                    value: missingUserIds,
                }
            })
        },
        fetchPolicy: 'cache-and-network',
        nextFetchPolicy: 'cache-first'
    });

    const usersById = useMemo(() => (
        (usersData?.users?.data || []).reduce((acc, user) => ({
            ...acc,
            [ user.id ]: user,
        }), {})
    ), [ usersData ]);

    const { data: languageCacheData } = useQuery(GET_TRANSLATION_AI_LABEL_REQUESTS, {
        variables: languageCacheHelper.labelRequestVariables,
        fetchPolicy: 'cache-and-network',
        nextFetchPolicy: 'cache-first'
    });

    const languageCacheStatuses = useMemo(() => (
        languageCacheHelper.buildStatuses(languageCacheData?.translateAiRequests?.data || [])
    ), [ languageCacheData ]);

    const languageCacheStatusMap = useMemo(() => (
        languageCacheHelper.toMap(languageCacheStatuses)
    ), [ languageCacheStatuses ]);

    const { data: analyticsData } = useQuery(GET_TRANSLATION_AI_REQUESTS, {
        variables: {
            text: debouncedSearchText,
            first: analyticsRowsLimit,
            page: 1,
            where: filters,
            orderBy: [ { column: "CREATED_AT", order: "DESC" } ],
        },
        fetchPolicy: 'cache-and-network',
        nextFetchPolicy: 'cache-first'
    });

    const analyticsRecords = analyticsData?.translateAiRequests?.data || [];

    return (
        <PageWrapDefault
            className="page-settings page-translation-ai-requests"
            dataExist={ true }
            title={ <Localize>PAGES.Title_TranslationAiRequests</Localize> }
        >
            <div className="translation-ai-analytics-layout">
                <div className="translation-ai-table-panel">
                    <TranslationAiLanguageRequestSummary
                        records={ analyticsRecords }
                        languageCacheStatusMap={ languageCacheStatusMap }
                    />

                    <Tables.Main
                        model="translateAiRequests"
                        query={ GET_TRANSLATION_AI_REQUESTS }
                        varSet={ { perPage: 25 } }
                        routeUrl={ `${ routeUrl }` }
                        currentPage={ match ? parseInt(match.params.pageNum) : 1 }
                        searchText={ searchText }
                        objFilter={ filters }
                        objOrderBy={ [ { column: "CREATED_AT", order: "DESC" } ] }
                        tableHelper={ tableHelper }
                        tableLayout="auto"
                        extraClass="translation-ai-compact-table"
                        onData={ setRecords }
                        currentObj={ { usersById, languageCacheStatusMap } }
                    >
                        <div className="table-action-bar">
                            <div className="col">
                                <Settings.Filters.TranslationAiTable
                                    setSearchText={ setSearchText }
                                    setFilters={ setFilters }
                                    dateColumn="CREATED_AT"
                                    searchPlaceholder="SEARCH.Input_Placeholder_TranslationAiRequests"
                                    showRequestFilters={ true } />
                            </div>
                        </div>
                    </Tables.Main>
                </div>
                <div className="translation-ai-chart-panel">
                    <Settings.Charts.TranslationAi.Requests records={ analyticsRecords } />
                </div>
            </div>
        </PageWrapDefault>
    );
};

export default TranslationAiRequestsPage;
